import { API_HEADERS, AUTH_PATH_PREFIXES, DEFAULT_REQUEST_TIMEOUT_MS } from "@sagot/constants";
import { transportError } from "./errors";
import type { SessionManager } from "./session";

export interface AuthFetchOptions {
  session: SessionManager;
  baseUrl: string;
  /** Current workspace id (resolved from the `/w/:ws` slug); sent as `X-Workspace-Id`. */
  getWorkspaceId?: () => string | null | undefined;
  fetch?: typeof fetch;
  timeoutMs?: number;
  /** Called when a request is still 401 after one refresh (the session is gone). */
  onUnauthorized?: () => void;
}

function basePath(baseUrl: string): string {
  const origin = typeof location === "undefined" ? "http://localhost" : location.origin;
  return new URL(baseUrl, origin).pathname.replace(/\/$/, "");
}

function isAuthFlow(request: Request, prefix: string): boolean {
  const path = new URL(request.url).pathname;
  const apiPath = path.startsWith(prefix) ? path.slice(prefix.length) : path;
  return AUTH_PATH_PREFIXES.some((p) => apiPath.startsWith(p));
}

interface Attempt {
  token: string | null;
  workspaceId: string | null | undefined;
}

/**
 * `fetch` for openapi-fetch: bearer + workspace header injection, cookie credentials, timeout,
 * and a single refresh-then-retry on 401. Transport failures throw a normalized `ApiError`.
 */
export function createAuthFetch(options: AuthFetchOptions): (request: Request) => Promise<Response> {
  const fetchImpl = options.fetch ?? globalThis.fetch.bind(globalThis);
  const timeoutMs = options.timeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS;
  const prefix = basePath(options.baseUrl);

  async function send(request: Request, attempt: Attempt): Promise<Response> {
    const headers = new Headers(request.headers);
    if (attempt.token) headers.set(API_HEADERS.authorization, `Bearer ${attempt.token}`);
    if (attempt.workspaceId && !headers.has(API_HEADERS.workspace)) headers.set(API_HEADERS.workspace, attempt.workspaceId);
    const controller = new AbortController();
    const state = { timedOut: false };
    const timer = setTimeout(() => {
      state.timedOut = true;
      controller.abort();
    }, timeoutMs);
    const forwardAbort = () => {
      controller.abort();
    };
    request.signal.addEventListener("abort", forwardAbort, { once: true });
    try {
      return await fetchImpl(new Request(request, { headers, credentials: "include", signal: controller.signal }));
    } catch {
      if (state.timedOut) throw transportError("timeout");
      throw transportError(request.signal.aborted ? "aborted" : "network");
    } finally {
      clearTimeout(timer);
      request.signal.removeEventListener("abort", forwardAbort);
    }
  }

  return async (request: Request) => {
    const authFlow = isAuthFlow(request, prefix);
    const retryCopy = authFlow ? null : request.clone();
    const workspaceId = options.getWorkspaceId?.();
    const token = authFlow ? null : ((await options.session.ensureFresh())?.value ?? null);
    const response = await send(request, { token, workspaceId });
    if (response.status !== 401 || !retryCopy || !token) return response;
    const refreshed = await options.session.refresh();
    if (!refreshed) {
      options.onUnauthorized?.();
      return response;
    }
    const retried = await send(retryCopy, { token: refreshed.value, workspaceId });
    if (retried.status === 401) {
      options.session.clear();
      options.onUnauthorized?.();
    }
    return retried;
  };
}
